// Single-producer single-consumer ring of fixed-size f64 records over a SharedArrayBuffer
// (lock-free: Atomics on the read/write indices). Used between the main thread and the audio
// worklet when the page is cross-origin isolated; otherwise both sides fall back to postMessage.

export interface RingBuffers {
  sab: SharedArrayBuffer;
  capacity: number;
  stride: number;
}

const HEADER_BYTES = 16; // Int32 read index, Int32 write index (+ padding to 8-byte alignment)

export function createRing(capacity: number, stride: number): RingBuffers {
  const sab = new SharedArrayBuffer(HEADER_BYTES + capacity * stride * 8);
  return { sab, capacity, stride };
}

export class Ring {
  private idx: Int32Array;
  private data: Float64Array;
  readonly capacity: number;
  readonly stride: number;

  constructor(r: RingBuffers) {
    this.idx = new Int32Array(r.sab, 0, 2);
    this.data = new Float64Array(r.sab, HEADER_BYTES, r.capacity * r.stride);
    this.capacity = r.capacity;
    this.stride = r.stride;
  }

  /** Write one record (first `stride` values of `rec`). Returns false if full. */
  push(rec: ArrayLike<number>, offset = 0): boolean {
    const w = Atomics.load(this.idx, 1);
    const r = Atomics.load(this.idx, 0);
    const next = (w + 1) % this.capacity;
    if (next === r) return false;
    const base = w * this.stride;
    for (let i = 0; i < this.stride; i++) this.data[base + i] = rec[offset + i] ?? 0;
    Atomics.store(this.idx, 1, next);
    return true;
  }

  /** Read one record into `out`. Returns false if empty. */
  pop(out: Float64Array): boolean {
    const r = Atomics.load(this.idx, 0);
    const w = Atomics.load(this.idx, 1);
    if (r === w) return false;
    const base = r * this.stride;
    for (let i = 0; i < this.stride; i++) out[i] = this.data[base + i];
    Atomics.store(this.idx, 0, (r + 1) % this.capacity);
    return true;
  }
}

/** Realtime commands main → worklet (records of 4 values: op, a, b, c). */
export const CMD_STRIDE = 4;
export const enum Op {
  NoteOn = 1,
  NoteOff = 2,
  NoteRepeat = 3,
  AllNotesOff = 4,
}
